import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "window-cleaning-web-design-colorado",
  metaTitle: "Window Cleaning Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom window cleaning web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Window Cleaning Web Design Colorado",
  eyebrow: "Window Cleaning Web Design — Colorado",
  h1: (
    <>
      Window Cleaning <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded window cleaning websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Window Cleaning websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first window cleaning sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Window Cleaning web design in Colorado",
    body: "Window Cleaning web design is the practice of building conversion-focused, mobile-first websites for window cleaning businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with instant quote calculator above the fold." },
    { title: "Pane-count quote calculator", body: "Window-count calculator that quotes and books in under 60 seconds." },
    { title: "Service pages per type", body: "Residential, commercial, post-construction, gutters, screens, hard-water — each ranks." },
    { title: "Recurring plan signup", body: "Quarterly/biannual recurring service signup with autopay setup." },
    { title: "Insurance + bonding badges", body: "Insurance, bonding, and reviews above the fold." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Instant pricing wins the booking", body: "Window cleaning is a commodity until you remove price friction. Calculators book 3x more." },
    { heading: "Recurring is the LTV play", body: "Quarterly autopay signup drives 4x customer lifetime value vs one-time cleans." },
    { heading: "Hard-water and post-construction are high-margin niches", body: "Specialty pages capture premium searches most cleaners ignore." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a window cleaning website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a window cleaning website?", a: "Typical timeline is 4–6 weeks for a 12–20 page window cleaning site with full service and service-area pages. Larger multi-location window cleaning businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs window cleaning businesses?", a: "No — we build window cleaning sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Window Cleaning Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for window cleaning businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/window-cleaning-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
